import DatePicker from 'react-datepicker';
import { MenuItem, Select } from '@mui/material';
import moment from 'moment-timezone';
import { Typography } from '@design-system/components/typography/typography';
import { ExistingScheduleTime } from '../model/scheduleGroups.domain';
import {
  changeAmPm,
  changeDate,
  changeHour,
  changeMinutes,
  dayTimeOptions,
  keepWallClock,
  timezoneOptions,
  toIsoString,
} from '../model/scheduleTime';
import {
  ExistingTimeSelect,
  JoinedGroupNote,
  LockedWhenJoined,
} from './ExistingTimeSelect';
import {
  centered,
  stack,
  timeColumn,
  timePartSelect,
  timeRow,
  timeZoneSelect,
} from './schedulePicker.css';

type SchedulePickerProps = {
  publishedAt: string | undefined;
  timeZone: string;
  onChange: (publishedAt: string) => void;
  onTimeZoneChange: (timeZone: string) => void;
  times: Array<ExistingScheduleTime>;
  joined: ExistingScheduleTime | undefined;
  onJoin: (time: ExistingScheduleTime) => void;
  onClear: () => void;
  programName: string | undefined;
  joiningCount: number;
};

const leadingZeroFormatter = new Intl.NumberFormat(undefined, {
  minimumIntegerDigits: 2,
});

const timeZoneOptionsWith = (timeZone: string) =>
  timezoneOptions.some((option) => option.value === timeZone)
    ? timezoneOptions
    : [...timezoneOptions, { value: timeZone, label: timeZone }];

const NumberSelect = (p: {
  value: number;
  count: number;
  onChange: (value: number) => void;
}) => (
  <Select className={timePartSelect} value={p.value}>
    {[...Array(p.count).keys()].map((v) => (
      <MenuItem key={v} value={v} onClick={() => p.onChange(v)}>
        <Typography variant="body" size="large">
          {leadingZeroFormatter.format(v)}
        </Typography>
      </MenuItem>
    ))}
  </Select>
);

const LabelSelect = (p: {
  value: string;
  options: Array<{ value: string; label: string }>;
  className: string;
  onChange: (value: string) => void;
}) => (
  <Select className={p.className} value={p.value}>
    {p.options.map(({ label, value }) => (
      <MenuItem key={value} value={value} onClick={() => p.onChange(value)}>
        <Typography variant="body" size="large">
          {label}
        </Typography>
      </MenuItem>
    ))}
  </Select>
);

export const SchedulePicker = (p: SchedulePickerProps) => {
  const publishedMoment = moment.tz(p.publishedAt, p.timeZone);
  const ampm = publishedMoment.format('A');
  const hours =
    ampm === 'PM' ? publishedMoment.hours() - 12 : publishedMoment.hours();

  const change = (next: moment.Moment) => p.onChange(toIsoString(next));

  const changeTimeZone = (timeZone: string) => {
    if (p.publishedAt)
      p.onChange(keepWallClock(p.publishedAt, p.timeZone, timeZone));
    p.onTimeZoneChange(timeZone);
  };

  return (
    <div className={stack}>
      {p.times.length > 0 && (
        <ExistingTimeSelect
          times={p.times}
          joined={p.joined}
          onJoin={p.onJoin}
          onClear={p.onClear}
          timeZone={p.timeZone}
          programName={p.programName}
        />
      )}
      <LockedWhenJoined joined={p.joined}>
        <div className={centered}>
          <DatePicker
            inline
            selected={
              new Date(
                publishedMoment.year(),
                publishedMoment.month(),
                publishedMoment.date(),
              )
            }
            onChange={(date: Date | null) => {
              if (date !== null) change(changeDate(publishedMoment, date));
            }}
            minDate={new Date()}
          />
        </div>
      </LockedWhenJoined>
      {p.joined && (
        <JoinedGroupNote
          time={p.joined}
          timeZone={p.timeZone}
          joiningCount={p.joiningCount}
        />
      )}
      <LockedWhenJoined joined={p.joined}>
        <div className={timeColumn}>
          <LabelSelect
            className={timeZoneSelect}
            value={p.timeZone}
            options={timeZoneOptionsWith(p.timeZone)}
            onChange={changeTimeZone}
          />
          <div className={timeRow}>
            <NumberSelect
              value={hours}
              count={12}
              onChange={(hour) => change(changeHour(publishedMoment, hour))}
            />
            <Typography>:</Typography>
            <NumberSelect
              value={publishedMoment.minutes()}
              count={60}
              onChange={(minutes) =>
                change(changeMinutes(publishedMoment, minutes))
              }
            />
            <LabelSelect
              className={timePartSelect}
              value={ampm}
              options={dayTimeOptions}
              onChange={(value) => change(changeAmPm(publishedMoment, value))}
            />
          </div>
        </div>
      </LockedWhenJoined>
    </div>
  );
};
